import React, { useState, useEffect, useCallback } from "react";
import SiemTablero from "./SiemTablero";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import WazuhAgentsPanel from "./WazuhAgentsPanel";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

// Marca del producto. Viene del entorno para que un mismo codigo sirva en SegurIA y CostillaTech.
const MARCA = process.env.REACT_APP_BRAND_NAME || "SegurIA";

const SEVERITY_COLORS = {
  critical: { bg: "bg-red-100", text: "text-red-800", border: "border-red-300", dot: "bg-red-500" },
  high: { bg: "bg-orange-100", text: "text-orange-800", border: "border-orange-300", dot: "bg-orange-500" },
  medium: { bg: "bg-yellow-100", text: "text-yellow-800", border: "border-yellow-300", dot: "bg-yellow-500" },
  low: { bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-300", dot: "bg-blue-500" },
  info: { bg: "bg-gray-100", text: "text-gray-600", border: "border-gray-300", dot: "bg-gray-400" },
};

const getSevColor = (sev) => SEVERITY_COLORS[String(sev || "info").toLowerCase()] || SEVERITY_COLORS.info;

// ── KPI Card ──
const KPI = ({ label, value, sub, icon, color }) => (
  <div data-testid={`siem-kpi-${label.toLowerCase().replace(/\s/g, "-")}`}
    className={`rounded-2xl border p-5 transition-all hover:shadow-md ${color}`}>
    <div className="flex items-center gap-3 mb-2">
      <div className="text-2xl">{icon}</div>
      <p className="text-xs font-medium uppercase tracking-wider opacity-70">{label}</p>
    </div>
    <p className="text-3xl font-bold">{value}</p>
    {sub && <p className="text-xs mt-1 opacity-60">{sub}</p>}
  </div>
);

// ── Status Indicator ──
const WazuhStatus = ({ connected, version }) => (
  <div data-testid="wazuh-status" className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${connected ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
    <span className={`w-2.5 h-2.5 rounded-full ${connected ? "bg-green-500 animate-pulse" : "bg-red-500"}`}></span>
    {connected ? `Wazuh v${version || "?"} Conectado` : "Wazuh Desconectado"}
  </div>
);

// ── Monitor Card ──
const MonitorCard = ({ monitor, onDeactivate }) => {
  const sev = getSevColor(monitor.severity);
  return (
    <div data-testid={`monitor-${monitor.monitor_id}`}
      className={`bg-white rounded-xl border ${monitor.status === "active" ? "border-l-4 border-l-red-500" : "border-l-4 border-l-green-400"} p-4 transition-all hover:shadow-sm`}>


      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-bold px-2 py-0.5 rounded ${sev.bg} ${sev.text}`}>{String(monitor.severity || "N/A").toUpperCase() || "N/A"}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${monitor.status === "active" ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"}`}>
              {monitor.status === "active" ? "Monitoreando" : "Resuelto"}
            </span>
          </div>
          <p className="text-sm font-semibold text-gray-800 leading-snug" title={monitor.description}>{monitor.description || "Sin descripcion"}</p>
          {monitor.cve_id && <span className="text-xs px-2 py-0.5 rounded bg-purple-50 text-purple-700 font-mono mt-1 inline-block">{monitor.cve_id}</span>}
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
            {monitor.target_ip && <span className="bg-gray-100 px-2 py-0.5 rounded text-gray-600">{monitor.target_ip}{monitor.target_port ? ":" + monitor.target_port : ""}</span>}
            <span>{monitor.alerts_count || 0} alertas</span>
            <span>{monitor.created_at?.split("T")[0]}</span>
          </div>
        </div>
        {monitor.status === "active" && (
          <button data-testid={`deactivate-${monitor.monitor_id}`}
            onClick={() => onDeactivate(monitor.monitor_id)}
            className="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 font-medium transition">
            Parchado
          </button>
        )}
      </div>
    </div>
  );
};

// ── Create Monitor Dialog ──
const CreateMonitorDialog = ({ onClose, onSave, vulnerabilities }) => {
  const [form, setForm] = useState({
    target_ip: "", target_port: "", cve_id: "", severity: "medium", description: "", vulnerability_id: "",
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.target_ip.trim()) e.target_ip = "IP del target requerida";
    if (!form.description.trim()) e.description = "Descripcion requerida";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    await onSave({ ...form, target_port: form.target_port ? parseInt(form.target_port) : null });
    setSaving(false);
  };

  const handleSelectVuln = (vuln) => {
    setForm({
      ...form,
      target_ip: vuln.target_ip || vuln.ip || "",
      target_port: vuln.port ? String(vuln.port) : "",
      cve_id: vuln.cve_id || vuln.cve || "",
      severity: vuln.severity || vuln.risk_level || "medium",
      description: vuln.title || vuln.description || "",
      vulnerability_id: vuln.id || "",
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-red-700 to-red-900 text-white px-6 py-5 rounded-t-2xl">
          <h2 className="text-lg font-bold">Nuevo Monitor de Perimetro</h2>
          <p className="text-red-200 text-xs mt-1">Vigilar una vulnerabilidad hasta que se remedie</p>
        </div>
        <div className="p-6 space-y-4">
          {/* Import from vulns */}
          {vulnerabilities && vulnerabilities.length > 0 && (
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Importar desde vulnerabilidad detectada</label>
              <select data-testid="import-vuln-select" onChange={(e) => {
                const v = vulnerabilities.find(vv => (vv.id || vv._id) === e.target.value);
                if (v) handleSelectVuln(v);
              }} className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50">
                <option value="">-- Seleccionar vulnerabilidad --</option>
                {vulnerabilities.map((v) => (
                  <option key={v.id || v._id} value={v.id || v._id}>{v.cve_id || v.cve || "N/A"} - {(v.title || v.description || "").substring(0, 60)}</option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">IP Objetivo *</label>
              <input data-testid="monitor-ip" value={form.target_ip} onChange={(e) => { setForm({ ...form, target_ip: e.target.value }); setErrors(p => ({ ...p, target_ip: undefined })); }}
                className={`w-full border rounded-lg px-3 py-2 text-sm ${errors.target_ip ? "border-red-400 bg-red-50" : ""}`} placeholder="192.168.1.1" />
              {errors.target_ip && <p className="text-xs text-red-500 mt-1">{errors.target_ip}</p>}
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Puerto</label>
              <input data-testid="monitor-port" type="number" value={form.target_port} onChange={(e) => setForm({ ...form, target_port: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="8080" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">CVE ID</label>
              <input data-testid="monitor-cve" value={form.cve_id} onChange={(e) => setForm({ ...form, cve_id: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="CVE-2024-XXXX" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Severidad</label>
              <select data-testid="monitor-severity" value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-white">
                <option value="critical">Critica</option>
                <option value="high">Alta</option>
                <option value="medium">Media</option>
                <option value="low">Baja</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Descripcion *</label>
            <textarea data-testid="monitor-description" value={form.description} onChange={(e) => { setForm({ ...form, description: e.target.value }); setErrors(p => ({ ...p, description: undefined })); }}
              rows={2} className={`w-full border rounded-lg px-3 py-2 text-sm ${errors.description ? "border-red-400 bg-red-50" : ""}`} placeholder="Describa la vulnerabilidad a monitorear..." />
            {errors.description && <p className="text-xs text-red-500 mt-1">{errors.description}</p>}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">Cancelar</button>
            <button data-testid="save-monitor" onClick={handleSave} disabled={saving}
              className="px-5 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:bg-red-300">
              {saving ? "Creando..." : "Activar Monitor"}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ── Alert Row ──
const wazuhLevelToSev = (level) => {
  const n = typeof level === "number" ? level : parseInt(level) || 0;
  if (n >= 12) return "critical";
  if (n >= 8) return "high";
  if (n >= 4) return "medium";
  if (n >= 1) return "low";
  return "info";
};

const ALERT_TYPE_META = {
  NEW_FINDING: { label: "NUEVO HALLAZGO", cls: "bg-red-50 text-red-600 border-red-200" },
  NEW_CVE: { label: "CVE NUEVA", cls: "bg-red-50 text-red-600 border-red-200" },
  NEW_EXPOSED_PORT: { label: "PUERTO EXPUESTO", cls: "bg-orange-50 text-orange-600 border-orange-200" },
  LOOKALIKE_DOMAIN: { label: "DOMINIO SIMILAR", cls: "bg-purple-50 text-purple-600 border-purple-200" },
  LEAKED_CREDENTIAL: { label: "CREDENCIAL FILTRADA", cls: "bg-purple-50 text-purple-600 border-purple-200" },
  VULN_PERSISTS: { label: "SIN REMEDIAR", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  VULN_WORSENED: { label: "AGRAVADO", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  VULN_REMEDIATED: { label: "REMEDIADO", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  VULN_REGRESSION: { label: "REGRESION", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  WAZUH_AGENT_EVENT: { label: "EVENTO AGENTE", cls: "bg-blue-50 text-blue-600 border-blue-200" },
};

const SCAN_TYPE_LABELS = {
  osint: "OSINT",
  dark_web: "Dark Web",
  brand_impersonation: "Brand Impersonation",
  network_enum: "Network Enum",
  advanced_web: "Web Scanner",
  security_posture: "Security Posture",
  api_security: "API Security",
  ai_bot_security: "AI Bot Security",
  ai_attack_readiness: "AI Attack Readiness",
  microsegmentation: "Microsegmentacion",
  usb_policy: "Politica USB",
  internal_agent: "Agente Interno",
};

const AlertRow = ({ alert }) => {
  const [open, setOpen] = React.useState(false);
  const isWazuh = alert.source === "wazuh" || alert.rule?.level !== undefined;
  const level = alert.rule?.level ?? alert.severity;
  const sevName = typeof level === "number" || (level !== null && level !== undefined && level !== "" && !isNaN(Number(level)))
    ? wazuhLevelToSev(level)
    : String(level || "info").toLowerCase();
  const sev = getSevColor(sevName);
  const g = alert.guidance || null;
  const isConfig = g?.category === "config";
  const meta = isConfig
    ? { label: "CONFIG. AGENTE", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" }
    : (ALERT_TYPE_META[alert.alert_type] || { label: isWazuh ? "EVENTO AGENTE" : "ALERTA", cls: "bg-gray-50 text-gray-600 border-gray-200" });
  const title = alert.title || alert.rule?.description || alert.description || alert.message || "Alerta SIEM";
  const target = alert.target || alert.target_ip || alert.agent?.name || "";
  const origin = alert.source === "wazuh"
    ? ("Wazuh" + (alert.agent?.name ? " / " + alert.agent.name : ""))
    : (SCAN_TYPE_LABELS[alert.scan_type] || alert.scan_type || MARCA);
  const detail = alert.detail || alert.rule?.description || "";

  return (
    <div data-testid="siem-alert-row" className="border-b border-gray-50 hover:bg-gray-50/50 text-sm">
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => setOpen(!open)}>
        <span className={"w-2 h-2 rounded-full flex-shrink-0 " + sev.dot}></span>
        <span className="text-xs text-gray-400 flex-shrink-0 w-36">{(alert.timestamp || alert["@timestamp"] || "").substring(0, 19).replace("T", " ")}</span>
        <span className={"text-[10px] font-semibold px-2 py-0.5 rounded border flex-shrink-0 w-40 text-center " + meta.cls}>{meta.label}</span>
        <span className="flex-1 min-w-0">
          <span className="block text-gray-800 truncate" title={title}>
            {title}
            {alert._repeticiones > 1 && (
              <span data-testid="siem-alert-repeticiones"
                title={"Esta misma alerta llego " + alert._repeticiones + " veces"}
                className="ml-2 align-middle text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                &times;{alert._repeticiones}
              </span>
            )}
          </span>
          {target && <span className="block text-xs text-gray-400 truncate">{target}</span>}
        </span>
        <span className={"text-xs font-medium px-2 py-0.5 rounded flex-shrink-0 w-16 text-center " + sev.bg + " " + sev.text}>{sevName.toUpperCase()}</span>
        <span className="text-xs text-gray-500 flex-shrink-0 w-32 truncate" title={origin}>{origin}</span>
      </div>
      {open && (g || detail) && (
        <div data-testid="siem-alert-detail" className="px-4 pb-4 pl-[13.5rem] text-xs space-y-3">
          {g ? (
            <>
              {g.what && (
                <div>
                  <p className="font-semibold text-gray-700 uppercase tracking-wide text-[10px] mb-1">Que detecto</p>
                  <p className="text-gray-600 whitespace-pre-wrap">{g.what}</p>
                </div>
              )}
              {g.why && (
                <div>
                  <p className="font-semibold text-gray-700 uppercase tracking-wide text-[10px] mb-1">Por que importa</p>
                  <p className="text-gray-600 whitespace-pre-wrap">{g.why}</p>
                </div>
              )}
              {g.how && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="siem-alert-remediation">
                  <p className="font-semibold text-amber-900 uppercase tracking-wide text-[10px] mb-1">Como solucionarlo</p>
                  <p className="text-amber-900 whitespace-pre-wrap">{g.how}</p>
                </div>
              )}
              {g.evidence && (
                <div>
                  <p className="font-semibold text-gray-700 uppercase tracking-wide text-[10px] mb-1">
                    Que se comprobo{g.evidence_kind ? " (" + g.evidence_kind + ")" : ""}
                    {g.result ? <span className="ml-2 normal-case font-medium text-red-600">resultado: {g.result}</span> : null}
                  </p>
                  <pre className="bg-gray-900 text-green-300 rounded-lg p-3 text-[11px] font-mono overflow-x-auto whitespace-pre-wrap break-all">{g.evidence}</pre>
                </div>
              )}
              {g.compliance && Object.keys(g.compliance).length > 0 && (
                <div>
                  <p className="font-semibold text-gray-700 uppercase tracking-wide text-[10px] mb-1">Cumplimiento</p>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(g.compliance).map(([std, items]) => (
                      <span key={std} className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded text-[11px]">
                        {std} {Array.isArray(items) ? items.join(", ") : String(items)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {(g.mitre_techniques?.length > 0 || g.mitre_tactics?.length > 0) && (
                <div className="flex flex-wrap gap-1.5">
                  {(g.mitre_tactics || []).map(t => (
                    <span key={"ta" + t} className="px-2 py-0.5 bg-purple-50 text-purple-700 border border-purple-200 rounded text-[11px]">Tactica: {t}</span>
                  ))}
                  {(g.mitre_techniques || []).map(t => (
                    <span key={"te" + t} className="px-2 py-0.5 bg-purple-50 text-purple-700 border border-purple-200 rounded text-[11px]">Tecnica: {t}</span>
                  ))}
                </div>
              )}
              <div className="text-gray-400 pt-1 border-t border-gray-100 flex flex-wrap gap-x-4 gap-y-0.5">
                <span>Agente: {alert.agent?.name || "—"}{alert.agent?.id ? " (ID " + alert.agent.id + ")" : ""}</span>
                {g.policy && <span>Politica: {g.policy}</span>}
                {g.check_id && <span>Control: {g.check_id}</span>}
                {g.rule_id && <span>Regla Wazuh: {g.rule_id} (nivel {g.rule_level})</span>}
                {g.groups?.length > 0 && <span>Grupos: {g.groups.join(", ")}</span>}
                {g.score !== undefined && g.score !== null && <span>Score de la politica: {g.score}% ({g.passed}/{g.total_checks} aprobados)</span>}
              </div>
            </>
          ) : (
            <p className="text-gray-500 whitespace-pre-wrap">{detail}</p>
          )}
        </div>
      )}
    </div>
  );
};

// ══════════════ MAIN COMPONENT ══════════════
const SIEMDashboard = () => {

  // Company selector for superadmin
  const [currentUser, setCurrentUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [moduleStatus, setModuleStatus] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    axios.get(`${API}/auth/me`, { headers: headers() })
      .then(res => {
        setCurrentUser(res.data);
        if (res.data.role === "superadmin") {
          axios.get(`${API}/admin/companies`, { headers: headers() })
            .then(r => setCompanies(r.data.filter(c => c.name !== "SegurIA Platform" && c.name !== "SegurIA")))
            .catch(() => {});
        }
      }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedCompanyId || !currentUser || currentUser.role !== "superadmin") {
      setModuleStatus(null);
      return;
    }
    const token = localStorage.getItem("token");
    axios.get(`${API}/admin/company-modules/${selectedCompanyId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setModuleStatus(res.data))
      .catch(() => setModuleStatus(null));
  }, [selectedCompanyId, currentUser]);



  const cParam = () => currentUser?.role === "superadmin" && selectedCompanyId ? `company_id=${selectedCompanyId}` : "";

  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [monitors, setMonitors] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [vulnerabilities, setVulnerabilities] = useState([]);

  const headers = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const [importing, setImporting] = useState(false);
  const [alertFilter, setAlertFilter] = useState([]);  // vacio = todas
  const [alertTipo, setAlertTipo] = useState(null);    // null = cualquier tipo
  const [incluirRutina, setIncluirRutina] = useState(false);
  const [rutinaOmitida, setRutinaOmitida] = useState(0);
  const [alertSource, setAlertSource] = useState("all");
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const handleAutoImport = async () => {
    setImporting(true);
    try {
      const res = await axios.post(`${API}/siem/auto-import` + (cParam() ? `?${cParam()}` : ""), {}, { headers: headers() });
      alert(`${res.data.message} (${res.data.skipped} ya existian)`);
      fetchMonitors();
      fetchDashboard();
    } catch (err) {
      alert("Error: " + (err.response?.data?.detail || err.message));
    }
    setImporting(false);
  };

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/siem/dashboard` + (cParam() ? `?${cParam()}` : ""), { headers: headers() });
      setDashboard(res.data);
    } catch (err) { console.error("SIEM dashboard error:", err); }
    finally { setLoading(false); }
  }, [selectedCompanyId, currentUser]);

  const fetchMonitors = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/siem/monitors` + (cParam() ? `?${cParam()}` : ""), { headers: headers() });
      setMonitors(res.data.monitors || []);
    } catch (err) { console.error("SIEM monitors error:", err); }
  }, [selectedCompanyId, currentUser]);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/siem/alerts?limit=100` + (incluirRutina ? "&incluir_rutina=1" : "") + (cParam() ? `&${cParam()}` : ""), { headers: headers() });
      setAlerts(res.data.alerts || []);
      setRutinaOmitida(res.data.rutina_omitida || 0);
      setLastRefresh(new Date().toLocaleTimeString());
    } catch (err) { console.error("SIEM alerts error:", err); }
  }, [selectedCompanyId, currentUser, incluirRutina]);

  const fetchVulnerabilities = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vulnerabilities` + (cParam() ? `?${cParam()}` : ""), { headers: headers() });
      setVulnerabilities(res.data?.vulnerabilities || res.data || []);
    } catch (err) { console.error("Vulns error:", err); }
  }, [selectedCompanyId, currentUser]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard, selectedCompanyId]);
  useEffect(() => {
    if (activeTab === "monitors") { fetchMonitors(); fetchVulnerabilities(); }
    if (activeTab === "alerts") fetchAlerts();
  }, [activeTab, fetchMonitors, fetchAlerts, fetchVulnerabilities, selectedCompanyId]);

  const handleCreateMonitor = async (data) => {
    try {
      await axios.post(`${API}/siem/monitors` + (cParam() ? `?${cParam()}` : ""), data, { headers: headers() });
      setShowCreate(false);
      fetchMonitors();
      fetchDashboard();
    } catch (err) {
      alert("Error: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeactivate = async (monitorId) => {
    if (!window.confirm("Confirmar que la vulnerabilidad fue parcheada?")) return;
    try {
      await axios.put(`${API}/siem/monitors/${monitorId}/deactivate` + (cParam() ? `?${cParam()}` : ""), {}, { headers: headers() });
      fetchMonitors();
      fetchDashboard();
    } catch (err) { alert("Error al desactivar monitor"); }
  };

  const sevDeAlerta = (a) => {
    const level = a.rule?.level ?? a.severity;
    return typeof level === "number" || !isNaN(Number(level))
      ? wazuhLevelToSev(level)
      : String(level || "info").toLowerCase();
  };

  // El benchmark CIS del agente repite la misma regla cientos de veces. Se colapsan por
  // regla + objetivo y se muestra una sola fila con cuantas veces se repitio.
  const colapsarRepetidas = (lista) => {
    const vistas = new Map();
    for (const a of lista) {
      const clave = [a.source || "", a.rule?.id || a.alert_type || "", a.title || "",
                     a.agent_name || a.target || ""].join("|");
      const previa = vistas.get(clave);
      if (previa) {
        previa._repeticiones = (previa._repeticiones || 1) + 1;
      } else {
        vistas.set(clave, { ...a, _repeticiones: 1 });
      }
    }
    return Array.from(vistas.values());
  };

  const filteredAlerts = colapsarRepetidas(alerts.filter(a => {
    if (alertSource === "wazuh" && a.source !== "wazuh") return false;
    if (alertSource === "seguria" && a.source === "wazuh") return false;
    if (alertTipo && a.alert_type !== alertTipo) return false;
    if (!alertFilter.length) return true;
    return alertFilter.includes(sevDeAlerta(a));
  }));

  const alternarSeveridad = (key) => {
    if (key === "all") { setAlertFilter([]); return; }
    setAlertFilter((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const isSuperadmin = currentUser?.role === "superadmin";

  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: "\u25A3" },
    { id: "monitors", label: "Monitores", icon: "\u25CE" },
    { id: "alerts", label: "Alertas", icon: "\u26A0" },
    { id: "agents", label: "Agentes", icon: "\u25C8" },
    ...(isSuperadmin ? [{ id: "wazuh_live", label: "Wazuh Live", icon: "\u25C9" }] : []),
  ];

  useEffect(() => {
    if (activeTab === "wazuh_live" && currentUser && !isSuperadmin) setActiveTab("dashboard");
  }, [activeTab, currentUser, isSuperadmin]);

  if (loading && !dashboard) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-gray-900 to-red-900 rounded-2xl p-6 text-white">
          <h1 className="text-xl font-bold">SIEM - Monitoreo en Tiempo Real</h1>
          <p className="text-red-200 text-sm">Cargando...</p>
        </div>
        <div className="flex justify-center py-20"><div className="animate-spin h-10 w-10 border-4 border-red-500 border-t-transparent rounded-full" /></div>
      </div>
    );
  }

  const activeMonitors = monitors.filter(m => m.status === "active");
  const resolvedMonitors = monitors.filter(m => m.status === "resolved");

  return (
    <div data-testid="siem-dashboard" className="space-y-6">
      {/* Company Selector for SuperAdmin */}
      {currentUser?.role === "superadmin" && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <h3 className="text-sm font-semibold text-red-800 mb-2">Filtrar por Empresa</h3>
          <select data-testid="siem-company-selector" value={selectedCompanyId} onChange={(e) => setSelectedCompanyId(e.target.value)} className="w-full px-3 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 bg-white text-sm">
            <option value="">Todas las empresas (consolidado)</option>
            {companies.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
          </select>
          {selectedCompanyId && (
            <div className="mt-2">
              <p className="text-xs text-red-600">Mostrando: <strong>{companies.find(c => c.id === selectedCompanyId)?.name}</strong></p>
              {moduleStatus && !moduleStatus["siem"] && (
                <div className="mt-2 bg-yellow-50 border border-yellow-300 rounded-lg p-3 flex items-start gap-2">
                  <svg className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                  <div>
                    <p className="text-sm font-medium text-yellow-800">Modulo SIEM no habilitado</p>
                    <p className="text-xs text-yellow-600 mt-0.5">Este modulo no esta activo para esta empresa. Puede habilitarlo desde el Panel de Administracion.</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 to-red-900 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center backdrop-blur">
              <svg className="w-6 h-6 text-red-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold">SIEM - Monitoreo en Tiempo Real</h1>
              <p className="text-red-200 text-sm">Vigilancia de vulnerabilidades hasta su remediacion</p>
            </div>
          </div>
          <WazuhStatus connected={dashboard?.wazuh_connected} version={dashboard?.wazuh_version} />
        </div>
      </div>

      {/* Tabs */}
      <div data-testid="siem-tabs" className="flex gap-1 bg-gray-100 rounded-xl p-1">
        {tabs.map((tab) => (
          <button key={tab.id} data-testid={`siem-tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id ? "bg-white text-red-700 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
            <span className="mr-1.5">{tab.icon}</span>{tab.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {/* ── DASHBOARD TAB ── */}
        {activeTab === "dashboard" && dashboard && (
          <motion.div key="dash" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <SiemTablero
              tablero={dashboard.tablero}
              marca={MARCA}
              wazuhConectado={dashboard.wazuh_connected}
              monitoresResueltos={dashboard.resolved_monitors}
              onVerAlertas={(luz) => {
                // Cada tarjeta mide algo distinto. Antes el clic solo mandaba la via y fijaba
                // critica+alta, asi que "reaparecieron" mostraba los criticos abiertos: numeros
                // que no se correspondian con la tarjeta.
                const porLuz = {
                  criticos_abiertos:   { origen: "seguria", sev: ["critical", "high"], tipo: null },
                  agente_criticas:     { origen: "wazuh",   sev: ["critical", "high"], tipo: null },
                  regresiones:         { origen: "seguria", sev: [], tipo: "VULN_REGRESSION" },
                  monitores_persisten: { origen: "seguria", sev: [], tipo: "VULN_PERSISTS" },
                };
                const f = porLuz[luz.id] || { origen: luz.via === "agente" ? "wazuh" : "seguria", sev: [], tipo: null };
                setAlertSource(f.origen);
                setAlertFilter(f.sev);
                setAlertTipo(f.tipo);
                setActiveTab("alerts");
              }}
              onIrAgentes={() => setActiveTab("agents")}
            />

            {/* Claridad de conteos + estado de telemetria */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-3" data-testid="siem-clarity">
              {!dashboard.wazuh_agent_present && (
                <div className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="siem-no-telemetry">
                  <span className="text-slate-500 mt-0.5">&#9432;</span>
                  <div className="text-sm">
                    <p className="font-medium text-slate-800">Monitoreo perimetral &mdash; sin telemetria interna</p>
                    <p className="text-slate-500 text-xs mt-0.5">
                      Esta empresa no tiene un agente de Wazuh instalado. {MARCA} vigila la evolucion de los
                      hallazgos (nuevo, persiste, empeoro, remediado, regresion) desde afuera. Para detectar
                      explotacion en vivo se requiere instalar el agente de Wazuh en el servidor del cliente.
                    </p>
                    <button data-testid="siem-go-to-agents" onClick={() => setActiveTab("agents")}
                      className="mt-2 text-xs font-medium px-3 py-1.5 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition">
                      Instalar el agente
                    </button>
                  </div>
                </div>
              )}
              <div className="text-xs text-gray-500">
                <strong className="text-gray-700">Alertas</strong> = eventos sobre los hallazgos de los escaneres.{" "}
                <strong className="text-gray-700">Monitores</strong> = hallazgos que marcaste para seguimiento.
                Puede haber alertas sin monitores activos.
              </div>
              {dashboard.alerts_by_severity && (
                <div className="flex flex-wrap gap-2" data-testid="siem-alert-severity">
                  {["critical", "high", "medium", "low", "info"].map((s) => (
                    <span key={s} className={`text-xs px-2 py-1 rounded-lg ${getSevColor(s).bg} ${getSevColor(s).text}`}>
                      {s}: {dashboard.alerts_by_severity[s] || 0}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Recent monitors */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
              <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center">
                <h3 className="font-semibold text-gray-800">Monitores Recientes</h3>
                <div className="flex gap-2">
                  <button data-testid="btn-auto-import" onClick={handleAutoImport} disabled={importing}
                    className="text-xs px-3 py-1.5 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 font-medium disabled:opacity-50">
                    {importing ? "Importando..." : "Auto-importar Vulns"}
                  </button>
                  <button data-testid="btn-new-monitor-dash" onClick={() => { setActiveTab("monitors"); setShowCreate(true); }}
                    className="text-xs px-3 py-1.5 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 font-medium">+ Manual</button>
                </div>
              </div>
              <div className="divide-y divide-gray-50">
                {(dashboard.recent_monitors || []).length === 0 ? (
                  <p className="text-center text-sm text-gray-400 py-8">No hay monitores activos. Crea uno desde una vulnerabilidad detectada.</p>
                ) : (dashboard.recent_monitors || []).map((m) => (
                  <div key={m.monitor_id} className="px-5 py-3 flex items-center gap-3 text-sm">
                    <span className={`w-2 h-2 rounded-full ${m.status === "active" ? "bg-red-500 animate-pulse" : "bg-green-500"}`}></span>
                    <span className="font-medium text-gray-800 truncate max-w-md" title={m.description}>
                      {m.description || m.cve_id || "Hallazgo monitoreado"}
                    </span>
                    <span className="text-gray-400 whitespace-nowrap">
                      {[m.target_ip, m.target_port].filter(Boolean).join(":") || "—"}
                    </span>
                    <span className="text-gray-400 whitespace-nowrap">{m.alerts_count || 0} alertas</span>
                    <span className="flex-1"></span>
                    <span className={`text-xs px-2 py-0.5 rounded ${getSevColor(m.severity).bg} ${getSevColor(m.severity).text}`}>{m.severity}</span>
                  </div>
                ))}
              </div>
              {lastRefresh && <span className="text-xs text-gray-400">Actualizado: {lastRefresh}</span>}
            </div>

            {/* Recent alerts */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
              <div className="px-5 py-4 border-b border-gray-100">
                <h3 className="font-semibold text-gray-800">Alertas Recientes</h3>
              </div>
              {(dashboard.recent_alerts || []).length === 0 ? (
                <p className="text-center text-sm text-gray-400 py-8">Sin alertas registradas</p>
              ) : (dashboard.recent_alerts || []).map((a, i) => <AlertRow key={i} alert={a} />)}
            </div>
          </motion.div>
        )}

        {/* ── MONITORS TAB ── */}
        {activeTab === "monitors" && (
          <motion.div key="mon" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-800">Monitores de Perimetro</h3>
              <div className="flex gap-2">
                <button data-testid="btn-auto-import-mon" onClick={handleAutoImport} disabled={importing}
                  className="px-4 py-2 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition disabled:opacity-50">
                  {importing ? "Importando..." : "Auto-importar desde Escaneos"}
                </button>
                <button data-testid="btn-new-monitor" onClick={() => { fetchVulnerabilities(); setShowCreate(true); }}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition">+ Manual</button>
              </div>
            </div>

            {activeMonitors.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-red-700 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>Activos ({activeMonitors.length})
                </h4>
                <div className="space-y-3">
                  {activeMonitors.map((m) => <MonitorCard key={m.monitor_id} monitor={m} onDeactivate={handleDeactivate} />)}
                </div>
              </div>
            )}

            {resolvedMonitors.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-green-700 mb-3">Resueltos ({resolvedMonitors.length})</h4>
                <div className="space-y-3">
                  {resolvedMonitors.map((m) => <MonitorCard key={m.monitor_id} monitor={m} onDeactivate={handleDeactivate} />)}
                </div>
              </div>
            )}

            {monitors.length === 0 && (
              <div className="text-center py-12 bg-gray-50 rounded-2xl border border-gray-200">
                <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <p className="text-gray-500 font-medium">Sin monitores activos</p>
                <p className="text-gray-400 text-sm mt-1">Crea un monitor desde una vulnerabilidad detectada por {MARCA}</p>
              </div>
            )}
          </motion.div>
        )}

        {/* -- ALERTS TAB -- */}
        {activeTab === "alerts" && (
          <motion.div key="alerts" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <div>
                <p className="text-sm font-medium text-blue-800">Ciclo de vida de vulnerabilidades</p>
                <p className="text-xs text-blue-600 mt-0.5">Cada alerta refleja un cambio de estado de sus hallazgos: nuevos, agravados, sin remediar, remediados o regresiones. Si hay agentes instalados, lo que reportan aparece como "Evento Agente" (actividad en vivo) o "Config. Agente" (controles de configuracion que no se cumplen, con su remediacion). Haga clic en cualquier fila para ver el detalle completo.</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-semibold text-gray-800">Alertas SIEM ({filteredAlerts.length}){alerts.length > filteredAlerts.length && (<span className="ml-2 text-xs font-normal text-gray-400">agrupadas de {alerts.length}</span>)}</h3>
                <button data-testid="refresh-alerts" onClick={async () => { setRefreshing(true); await fetchAlerts(); setRefreshing(false); }} className="text-xs px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 font-medium transition">{refreshing ? "Cargando..." : "Actualizar"}</button>
              </div>
              <div data-testid="alert-source-filters" className="flex gap-1.5 mr-3">
                {[{ key: "all", label: "Todo" }, { key: "seguria", label: MARCA }, { key: "wazuh", label: "Agente" }].map(f => (
                  <button key={f.key} data-testid={"alert-source-" + f.key}
                    onClick={() => setAlertSource(f.key)}
                    className={alertSource === f.key ? "px-3 py-1 text-xs rounded-lg font-medium transition bg-slate-800 text-white" : "px-3 py-1 text-xs rounded-lg font-medium transition bg-gray-100 text-gray-600 hover:bg-gray-200"}>
                    {f.label}
                  </button>
                ))}
              </div>
              <div data-testid="alert-filters" className="flex gap-1.5 items-center">
                {[{ key: "all", label: "Todas" }, { key: "critical", label: "Critica" }, { key: "high", label: "Alta" }, { key: "medium", label: "Media" }, { key: "low", label: "Baja" }].map(f => {
                  const activo = f.key === "all" ? !alertFilter.length : alertFilter.includes(f.key);
                  return (
                    <button key={f.key} data-testid={"alert-filter-" + f.key}
                      onClick={() => alternarSeveridad(f.key)}
                      title={f.key === "all" ? "Ver todas" : "Se pueden marcar varias a la vez"}
                      className={activo
                        ? "px-3 py-1 text-xs rounded-lg font-medium transition bg-red-600 text-white"
                        : "px-3 py-1 text-xs rounded-lg font-medium transition bg-gray-100 text-gray-600 hover:bg-gray-200"}>
                      {f.key !== "all" && (
                        <span className="mr-1.5">{activo ? "\u2713" : "\u25A1"}</span>
                      )}
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {(alertTipo || rutinaOmitida > 0 || incluirRutina) && (
              <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="alert-contexto">
                {alertTipo && (
                  <button data-testid="alert-tipo-chip" onClick={() => setAlertTipo(null)}
                    className="px-3 py-1 text-xs rounded-lg font-medium bg-slate-800 text-white hover:bg-slate-700 transition">
                    Solo {((ALERT_TYPE_META[alertTipo] || {}).label || alertTipo).toLowerCase()} &times;
                  </button>
                )}
                {(rutinaOmitida > 0 || incluirRutina) && (
                  <button data-testid="alert-rutina-toggle" onClick={() => setIncluirRutina(v => !v)}
                    title="Controles CIS y cambios de archivos de nivel medio: auditoria de rutina, no incidentes"
                    className={incluirRutina
                      ? "px-3 py-1 text-xs rounded-lg font-medium bg-slate-800 text-white hover:bg-slate-700 transition"
                      : "px-3 py-1 text-xs rounded-lg font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition"}>
                    {incluirRutina ? "Ocultar auditoria de rutina" : "Ver auditoria de rutina" + (rutinaOmitida ? " (" + rutinaOmitida.toLocaleString("es-AR") + ")" : "")}
                  </button>
                )}
              </div>
            )}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              {filteredAlerts.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  <p className="text-gray-500 font-medium" data-testid="alerts-vacio">{alertTipo ? ("Ninguna alerta de tipo " + ((ALERT_TYPE_META[alertTipo] || {}).label || alertTipo).toLowerCase()) : alertFilter.length ? "Sin alertas de los niveles elegidos" : "Sin alertas"}</p>
                  <p className="text-gray-400 text-sm mt-1">Las alertas apareceran cuando un escaneo detecte cambios en sus hallazgos</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50">
                  <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 text-xs text-gray-500 font-medium">
                    <span className="w-2"></span>
                    <span className="w-36">Fecha</span>
                    <span className="w-40 text-center">Tipo</span>
                    <span className="flex-1">Hallazgo / Objetivo</span>
                    <span className="w-16 text-center">Severidad</span>
                    <span className="w-32">Origen</span>
                  </div>
                  {filteredAlerts.map((a, i) => <AlertRow key={i} alert={a} />)}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* -- AGENTES WAZUH (telemetria interna del cliente) -- */}
        {activeTab === "agents" && (
          <WazuhAgentsPanel cParam={cParam()} currentUser={currentUser} onChanged={fetchDashboard} />
        )}

        {/* -- WAZUH LIVE DASHBOARD -- */}
        {activeTab === "wazuh_live" && isSuperadmin && (
          <motion.div key="wazuh" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <div className="bg-gradient-to-r from-blue-800 to-blue-900 rounded-2xl p-5 text-white flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold">Wazuh Dashboard en Vivo</h3>
                <p className="text-blue-200 text-sm">Consola completa del manager Wazuh. Muestra todos los agentes de la plataforma: uso exclusivo del equipo de {MARCA}.</p>
              </div>
              <div className="flex items-center gap-3">
                <span data-testid="wazuh-internal-badge" className="text-xs text-blue-200 bg-blue-700/50 px-3 py-1 rounded-lg">
                  Acceso tecnico interno &middot; solo superadmin
                </span>
                <a href="/wazuh/" target="_blank" rel="noopener noreferrer"
                  data-testid="wazuh-open-new-tab"
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium transition backdrop-blur">
                  Abrir en nueva ventana
                </a>
              </div>
            </div>
            <div data-testid="wazuh-iframe-container" className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm" style={{ height: "75vh" }}>
              <iframe
                data-testid="wazuh-iframe"
                src="/wazuh/app/wz-home"
                title="Wazuh Dashboard"
                className="w-full h-full border-0"
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-downloads"
              />
            </div>
          </motion.div>
        )}

      </AnimatePresence>

      {/* Create Monitor Dialog */}
      {showCreate && (
        <CreateMonitorDialog
          onClose={() => setShowCreate(false)}
          onSave={handleCreateMonitor}
          vulnerabilities={vulnerabilities}
        />
      )}
    </div>
  );
};

export default SIEMDashboard;
